import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  NormalizePhotoApi,
  NormalizedPhoto,
} from "services/photoNormalizationService";
import { PhotoNormalizationDialog } from "components/photoNormalization/PhotoNormalizationDialog";

export function usePhotoNormalization() {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<{
    original: File;
    result: NormalizedPhoto;
  } | null>(null);
  const pending = useRef<((result: NormalizedPhoto | null) => void) | null>(null);
  const controller = useRef<AbortController | null>(null);
  const busy = useRef(false);


  useEffect(
    () => () => {
      controller.current?.abort();
      pending.current?.(null);
      pending.current = null;
    },
    [],
  );

  const finish = (result: NormalizedPhoto | null) => {
    pending.current?.(result);
    pending.current = null;
    busy.current = false;
    setPreview(null);
  };

  // THIS FUNCTION REVIEWS THE PHOTO BY API NORMALIZING IT AND HANDLING THE UI STATE ACCORDINGLY.
  const reviewPhotoFun = async (file: File): Promise<NormalizedPhoto | null> => {
    if (busy.current) {
      toast.error("Please finish reviewing the current photo first.");
      return null;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image for the photo.");
      return null;
    }
    busy.current = true;
    const request = new AbortController();
    controller.current = request;
    setLoading(true);
    try {
      //API CALLING THE NORMALIZEPHOTO FUNCTION TO NORMALIZE THE PHOTO AND RETURN THE RESULT.
      const result = await NormalizePhotoApi(file, request.signal);
      if (request.signal.aborted) return null;
      toast.success(
        "Photo converted successfully. Please review it before accepting.",
      );
      return await new Promise<NormalizedPhoto | null>((resolve) => {
        pending.current = resolve;
        setPreview({ original: file, result });
        setLoading(false);
      });
    } catch (error) {
      if (!request.signal.aborted)
        toast.error(
          error instanceof Error ? error.message : "Failed to normalize photo.",
        );
      return null;
    } finally {
      busy.current = false;
      if (!request.signal.aborted) setLoading(false);
    }
  };

  return {
    reviewPhotoFun,
    photoLoading: loading,
    photoBusy: loading || preview !== null,
    photoDialog: preview ? (
      <PhotoNormalizationDialog
        original={preview.original}
        converted={preview.result.file}
        onAccept={() => finish(preview.result)}
        onReupload={() => finish(null)}
      />
    ) : null,
  };
}
